import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod/v3'
import { queryClient, trpc } from '@/lib/trpc'

// Router parses numeric-looking search values (?search=341) into numbers, so
// coerce back to string instead of failing validation (same as the Tests route).
const searchString = z.coerce.string().optional()

const controlsSearchSchema = z.object({
  type: z.enum(['controls', 'tests', 'frameworks', 'monitoring']).optional(),
  // Presentation only: named Views keep their filters while switching layout.
  layout: z.enum(['tree', 'list']).optional(),
  controlRef: searchString,
  intent: z.enum(['prepare', 'generate']).optional(),
  // RFC-157 Phase C framework filter — kept in the schema so type-safe links
  // (e.g. the Frameworks lens cards) can deep-link into a filtered Controls view.
  framework: searchString,
  // Filter state, owned by ControlsTestsPage and written to the URL by nuqs so
  // a filtered list is bookmarkable.
  search: searchString,
  status: searchString,
  assignee: searchString,
  labels: searchString,
  // RFC-234 inclusion (all | core | scope | extended).
  inclusion: searchString,
  // RFC-235: the tree filters test leaves with the Tests list's full set.
  // #1705 test kind (all | policy | requirement | automated | manual).
  kind: searchString,
  // RFC-226 provenance and the Hide N/A checkbox — JSON-parsed to booleans
  // before validation, like the /tests route declares them.
  auditorRequested: z.boolean().optional(),
  hideNa: z.boolean().optional(),
  // RFC-235 M2: the test open in the right-hand pane (a CES id).
  test: searchString,
  // The Control open in the same pane; restored by reload and Back/Forward.
  control: searchString,
  // Identifies an open saved view so equal filter sets select the right tab.
  view: searchString,
})

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/controls'
)({
  validateSearch: controlsSearchSchema,
  loader: ({ context, preload }) => {
    // Start after the protected route establishes workspace context, alongside
    // the lazy page and layout reads. Keep the page's no-program guard intact.
    if (preload || !context.workspace) return
    void queryClient.prefetchQuery(
      trpc.complianceProgram.getProgram.queryOptions()
    )
  },
  staticData: {
    pageHeader: {
      title: 'Controls',
      pad: false,
      scroll: false,
      hideVisibleTitle: true,
    },
  },
})
